import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { FormErrors } from 'modules/core/hooks/useForm'
import countryList from 'modules/core/lib/countryList'
import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import genders from 'modules/settings/store/collections/genders'

import { BasicInformationValues } from '../types'

interface Props {
  values: BasicInformationValues
  onChangeValue: (fieldName: string, fieldValue: string | undefined) => void
  employee: Employee
  readonly: boolean
  errors: FormErrors<BasicInformationValues>
}

const PersonalSection: React.FC<Props> = ({
  values,
  onChangeValue,
  readonly,
  employee,
  errors,
}) => {
  const sharePreferredName = employee.policy.can('sharePreferredName')
  const today = moment()
  const maxDate = today.clone().subtract(14, 'years')

  const genderOptions = () => {
    const legalEntityId = employeeLegalEntityId(employee)
    if (!legalEntityId) return []
    return genders.availablesIn(legalEntityId).map((item) => ({
      label: item.formattedName || '',
      value: item.name,
    }))
  }

  return (
    <Box gap="s24">
      <Box
        paddingBottom="s8"
        border={{
          bottom: {
            width: 's1',
            style: 'solid',
            color: 'grey300',
          },
        }}
      >
        <Text.Header2 color="grey1000" weight="medium">
          {i18n.t('ats.candidate_details.personal.label')}
        </Text.Header2>
      </Box>
      <Box flexDirection="row" gap="s24">
        <TextField
          mandatory
          label={i18n.t('employee_form.first_name')}
          value={values.firstName}
          readonly={readonly}
          onChange={(value) => onChangeValue('firstName', value)}
          error={errors.firstName}
        />
        <TextField
          mandatory
          label={i18n.t('employee_form.last_name')}
          value={values.lastName || ''}
          readonly={readonly}
          onChange={(value) => onChangeValue('lastName', value)}
          error={errors.lastName}
        />
        {sharePreferredName && (
          <TextField
            label={i18n.t('employee_form.preferred_name')}
            value={values.preferredName}
            readonly={readonly}
            onChange={(value) => onChangeValue('preferredName', value)}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t('employee_form.preferred_name_information')}
                icon="question"
              />
            }
          />
        )}
      </Box>
      <Box flexDirection="row" gap="s24">
        <DayPicker
          label={i18n.t('employee_form.birthday')}
          value={values.birthdayOn}
          maxDate={maxDate}
          readonly={readonly}
          onChange={(value) => onChangeValue('birthdayOn', value)}
        />
        <Select
          none={i18n.t('common.select')}
          readonly={readonly}
          onChange={(value) => onChangeValue('nationality', value)}
          label={i18n.t('employee_form.nationality')}
          options={countryList}
          value={values.nationality}
          mandatory
          error={errors.nationality}
        />
        <Select
          label={i18n.t('employee_form.gender')}
          readonly={readonly}
          options={genderOptions()}
          defaultValue={values.gender}
          onChange={(value) => onChangeValue('gender', value)}
          none={i18n.t('common.choose')}
        />
      </Box>
      <Box flexDirection="row" style={{ width: '260px' }}>
        {hasFeature('pronouns') && (
          <TextField
            label={i18n.t('employee_form.pronouns')}
            value={values.pronouns}
            readonly={readonly}
            onChange={(value) => onChangeValue('pronouns', value)}
            error={errors.pronouns}
            placeholder={i18n.t('employee_form.pronouns_placeholder')}
          />
        )}
      </Box>
    </Box>
  )
}

export default observer(PersonalSection)
